/**
 * Studio-timezone utilities.
 *
 * The studio operates in America/New_York. Railway containers run in
 * UTC. Any code that does Date.setHours, getDate, getDay, etc. without
 * a TZ wrapper will silently mis-classify "today", "now", and "what
 * day-of-week is this" — sometimes by 4–5 hours, occasionally landing
 * on the wrong calendar date.
 *
 * Use these helpers anywhere wall-clock studio time matters: business
 * hours, "is today", payroll period boundaries, holiday windows,
 * birthday matching, no-show follow-ups.
 *
 * Existing safe paths:
 *   - lib/sms.ts quiet-hours uses Intl directly — leave alone.
 *   - Pure ISO instants (`createdAt < someDate`) — UTC vs UTC, no fix needed.
 *   - hebcal HDate() math — calendar-aware, no fix needed.
 */

export const STUDIO_TZ = "America/New_York";

interface StudioParts {
  /** YYYY-MM-DD in studio TZ */
  dateStr: string;
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number; // 0-59
  /** 0 = Sunday, 1 = Monday, ..., 6 = Saturday */
  weekday: number;
  /** Minutes since midnight (0–1439) */
  mins: number;
}

// hourCycle "h23" (NOT hour12: false): Node 20's ICU resolves
// hour12: false to the h24 cycle, which renders midnight as "24:00"
// and poisoned studioStartOfDay's drift loop into returning the
// PREVIOUS day on every call. Node 22+ resolves to h23 so the bug
// only shows on prod/CI runtimes. Caught by studio-tz.test.ts on CI.
const FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: STUDIO_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  weekday: "short",
});

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function studioParts(d: Date = new Date()): StudioParts {
  const parts = FMT.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour")) % 24; // defense: some ICUs render midnight as "24"
  const minute = Number(get("minute"));
  const weekday = WEEKDAY_INDEX[get("weekday")] ?? 0;
  return {
    dateStr: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    year,
    month,
    day,
    hour,
    minute,
    weekday,
    mins: hour * 60 + minute,
  };
}

/** YYYY-MM-DD for the current calendar date in the studio's TZ. */
export function studioToday(): string {
  return studioParts().dateStr;
}

/** YYYY-MM-DD for the given Date interpreted in studio TZ. */
export function studioDateStr(d: Date): string {
  return studioParts(d).dateStr;
}

/** True iff the two Date instants land on the same calendar day in studio TZ. */
export function isSameStudioDate(a: Date, b: Date): boolean {
  return studioDateStr(a) === studioDateStr(b);
}

/**
 * Date instant representing the start (00:00) of the given calendar
 * date in studio TZ. Useful for Prisma `gte` / `lt` ranges that need
 * to bracket "today in NY", not "today in UTC".
 *
 * Implementation: takes the YYYY-MM-DD in studio TZ for the input,
 * then constructs a Date at the equivalent UTC instant. We use the
 * known offset from a probe Date — handles DST automatically because
 * we re-probe per call.
 */
/**
 * 00:00 studio time on the calendar day AFTER the given instant's studio
 * day — the exclusive upper bound for "today in NY" Prisma brackets.
 * +36h from the day's start always lands inside tomorrow even across
 * the 23/25-hour DST days.
 */
export function studioStartOfNextDay(d: Date = new Date()): Date {
  return studioStartOfDay(
    new Date(studioStartOfDay(d).getTime() + 36 * 60 * 60 * 1000)
  );
}

/**
 * 00:00 studio time on the given YYYY-MM-DD (as sent by
 * <input type="date">). The noon-UTC probe pins the calendar day —
 * noon UTC is always 7/8am in NY, never the previous/next day.
 */
export function studioMidnightOf(dateStr: string): Date {
  return studioStartOfDay(new Date(`${dateStr}T12:00:00Z`));
}

/**
 * 00:00 studio time on the day AFTER the given YYYY-MM-DD — exclusive
 * upper bound for "include the whole dateTo day" filters.
 */
export function studioMidnightAfter(dateStr: string): Date {
  return studioStartOfDay(
    new Date(new Date(`${dateStr}T12:00:00Z`).getTime() + 24 * 60 * 60 * 1000)
  );
}

/**
 * Instant for a studio wall-clock date + time — e.g. ("2026-07-04",
 * "14:30") → the Date at 2:30 PM ET on July 4. DST-safe via the same
 * drift-settle loop as studioStartOfDay. Invalid input returns null.
 */
export function studioDateTimeOf(dateStr: string, timeStr: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr) || !/^\d{1,2}:\d{2}$/.test(timeStr)) return null;
  const [h, m] = timeStr.split(":").map(Number);
  if (h > 23 || m > 59) return null;
  const targetMins = h * 60 + m;
  let guess = new Date(studioMidnightOf(dateStr).getTime() + targetMins * 60_000);
  for (let i = 0; i < 2; i++) {
    const p = studioParts(guess);
    if (p.dateStr === dateStr && p.mins === targetMins) return guess;
    const driftMins =
      (p.dateStr < dateStr ? -1_440 : p.dateStr > dateStr ? 1_440 : 0) +
      p.mins -
      targetMins;
    guess = new Date(guess.getTime() - driftMins * 60_000);
  }
  return guess;
}

export function studioStartOfDay(d: Date = new Date()): Date {
  const { year, month, day } = studioParts(d);
  // Iterate twice to settle DST: first guess assumes -5h offset; the
  // resulting wall-clock may shift by ±1h around the DST transition,
  // so we re-probe and correct. Two iterations is always sufficient.
  let guess = new Date(Date.UTC(year, month - 1, day, 5, 0, 0));
  for (let i = 0; i < 2; i++) {
    const p = studioParts(guess);
    const driftMins =
      (p.year - year) * 525_960 +
      (p.month - month) * 43_830 +
      (p.day - day) * 1_440 +
      p.hour * 60 +
      p.minute;
    if (driftMins === 0) return guess;
    guess = new Date(guess.getTime() - driftMins * 60_000);
  }
  return guess;
}
